import { useCallback, useState } from "react";
import { ShieldAlert, ShieldCheck, ShieldQuestion } from "lucide-react";
import { payApi, type Banner } from "../../../api/pay";
import { PageHeader } from "../../../components/ComingSoon";
import { cn } from "../../../components/ui";
import { LoadState, useLoad } from "../shared";
import { OverviewTab } from "./OverviewTab";
import { PeriodsTab } from "./PeriodsTab";
import { PolicyTab } from "./PolicyTab";
import { PreviewTab } from "./PreviewTab";
import { ProfilesTab } from "./ProfilesTab";
import { RatesTab } from "./RatesTab";

const TABS = [
  { id: "overview", label: "Pay overview" },
  { id: "periods", label: "Periods" },
  { id: "policy", label: "Pay Policy" },
  { id: "rates", label: "Roles & Rates" },
  { id: "profiles", label: "Employee pay profiles" },
  { id: "preview", label: "Calculation preview" },
] as const;
type TabId = (typeof TABS)[number]["id"];

export function PolicyBanner({ banner }: { banner: Banner }) {
  const Icon = banner.key === "ADVISOR" ? ShieldCheck : banner.key === "BUSINESS_APPROVED" ? ShieldQuestion : ShieldAlert;
  const tone = banner.key === "ADVISOR" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
    : banner.key === "BUSINESS_APPROVED" ? "border-sky-500/40 bg-sky-500/10 text-sky-200" : "border-amber-500/50 bg-amber-500/10 text-amber-200";
  return (
    <div className={cn("mb-8 flex items-start gap-3 rounded-sm border px-4 py-3 text-sm", tone)} data-testid="pay-policy-banner" role="status">
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>
        <p className="font-semibold" data-testid="pay-policy-banner-text">{banner.text}</p>
        <p className="text-xs opacity-80">{banner.advisor_required_note} Gross wages only — no payments, taxes, deductions or net pay in this app.</p>
      </div>
    </div>
  );
}

export default function AdminPay() {
  const [tab, setTab] = useState<TabId>("overview");
  const [nonce, setNonce] = useState(0);
  const loader = useCallback(() => payApi.policy(), [nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad(loader);
  const refresh = () => setNonce((n) => n + 1);
  return (
    <div data-testid="admin-pay-page">
      <PageHeader title="Pay Periods" />
      {data && <PolicyBanner banner={data.banner} />}
      <LoadState error={error} loading={!data && !error} testId="pay-policy-load" />
      <nav className="glass mb-8 inline-flex max-w-full flex-wrap gap-1 rounded-[22px] p-1" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} data-testid={`pay-tab-${t.id}`}
            className={cn("rounded-full border px-4 py-2 text-sm font-medium transition-[background-color,box-shadow,color,border-color] duration-200",
              tab === t.id ? "border-signal/70 bg-[rgba(255,29,0,0.14)] text-white shadow-[0_0_16px_-4px_rgba(255,29,0,0.6)]" : "border-transparent text-zinc-400 hover:bg-white/[0.06] hover:text-white")}>
            {t.label}
          </button>
        ))}
      </nav>
      {tab === "overview" && <OverviewTab />}
      {tab === "periods" && <PeriodsTab />}
      {tab === "policy" && data && <PolicyTab data={data} onChanged={refresh} />}
      {tab === "rates" && <RatesTab />}
      {tab === "profiles" && <ProfilesTab />}
      {tab === "preview" && <PreviewTab />}
    </div>
  );
}
